import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Loader2, Search, ArrowUpDown } from "lucide-react";

const SORT_OPTIONS = [
  { key: "created_at", label: "Recently signed up" },
  { key: "reports", label: "Most active (7d)" },
  { key: "caregivers", label: "Most caregivers" },
];

export default function PlatformAgencies() {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("created_at");

  useEffect(() => {
    setLoading(true);
    api
      .get("/platform/agencies", { params: { q: q || undefined, sort } })
      .then((r) => setAgencies(r.data))
      .finally(() => setLoading(false));
  }, [q, sort]);

  const totalReports7d = useMemo(
    () => agencies.reduce((s, a) => s + (a.stats?.reports_last_7d || 0), 0),
    [agencies]
  );

  return (
    <div className="space-y-6" data-testid="platform-agencies-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-amber-300/80 font-bold">Platform</p>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">Agencies</h1>
          <p className="mt-1 text-sm text-white/60">
            {agencies.length} agencies · {totalReports7d} reports in last 7 days
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or slug…"
              className="bg-white/5 border border-white/10 text-white placeholder:text-white/40 rounded-lg pl-9 pr-3 py-2 text-sm w-72 focus:border-amber-300 focus:outline-none"
              data-testid="agencies-search"
            />
          </div>
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="appearance-none bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 pr-8 text-sm focus:border-amber-300 focus:outline-none"
              data-testid="agencies-sort"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key} className="bg-[#0B1530]">{o.label}</option>
              ))}
            </select>
            <ArrowUpDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40 pointer-events-none" />
          </div>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-white/60">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : agencies.length === 0 ? (
        <div className="rounded-xl bg-white/5 border border-white/10 p-10 text-center text-white/60">
          No agencies match.
        </div>
      ) : (
        <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-white/40 bg-white/[0.03]">
                <th className="text-left px-4 py-2.5 font-medium">Agency</th>
                <th className="text-right px-3 py-2.5 font-medium">Admins</th>
                <th className="text-right px-3 py-2.5 font-medium">Caregivers</th>
                <th className="text-right px-3 py-2.5 font-medium">Clients</th>
                <th className="text-right px-3 py-2.5 font-medium">Reports (7d)</th>
                <th className="text-left px-3 py-2.5 font-medium">Last report</th>
                <th className="text-left px-3 py-2.5 font-medium">Joined</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {agencies.map((a) => (
                <tr
                  key={a.id}
                  className="hover:bg-white/[0.04] transition-colors cursor-pointer"
                  data-testid={`agency-row-${a.id}`}
                  onClick={(e) => {
                    // Don't double-navigate when clicking a nested link/button
                    if (e.target.closest("a, button")) return;
                    window.location.href = `/platform/agencies/${a.id}`;
                  }}
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/platform/agencies/${a.id}`}
                      className="font-medium text-white hover:text-amber-200"
                    >
                      {a.name}
                    </Link>
                    <div className="text-[11px] text-white/40 mt-0.5 font-mono">{a.slug}</div>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-white/80">{a.stats.admins}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-white/80">{a.stats.caregivers}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-white/80">{a.stats.clients}</td>
                  <td className="px-3 py-3 text-right tabular-nums font-semibold">
                    <span className={a.stats.reports_last_7d > 0 ? "text-emerald-300" : "text-white/40"}>
                      {a.stats.reports_last_7d}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-white/60 tabular-nums">
                    {a.stats.last_report_at
                      ? new Date(a.stats.last_report_at).toLocaleDateString()
                      : <span className="text-white/30 italic">never</span>}
                  </td>
                  <td className="px-3 py-3 text-xs text-white/60 tabular-nums">
                    {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <Link
                      to={`/platform/agencies/${a.id}`}
                      className="text-xs text-amber-200 hover:text-amber-100"
                      data-testid={`agency-open-${a.id}`}
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
